import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Button from '../AppButton/Button';
import FormInputText from './Inputs/FormInputText';
import { PersonalTestFormType } from './form-types';
import FormInputRadio from './Inputs/FormInputRadio';
import CheckboxItem from '../AppCheckboxItem/CheckboxItem';
import ImageBox from '../ImageBox/ImageBox';
import LabelValue from '../AppLabelValue/LabelValue';
import { useHistory } from 'react-router-dom';
import { routesURLs } from '../../Routes';
import { PersonalTestPackageType, PersonalTestType, SelectedPackageTestType } from '../../services/types';
import {courseTermDescription, formateNumber, getCookie, pushDataLayer, testTermDescription} from '../../services/utils';
import { format, parseISO } from 'date-fns';
import { cs } from 'date-fns/locale';

import personCoursesImage from '../../assets/images/icons/ic-personal-lessons.svg';
import { CoursesTypes, useCoursesState } from '../../contexts/courses';
import FormInputSelect from './Inputs/FormInputSelect';
import Modal from '../TheModal/Modal';
import ModalChoosePackageTest from '../TheModal/ModalChoosePackageTest';

let initialValues = {
    selectedLocation: 0,
    course: '',
    id: 0,
    student_name: '',
};

const attributes = {
    selectedLocation: {
        placeholder: 'Vyberte..',
        label: 'Vyberte místo konání',
        icon: 'pin-primary',
        options: [{ value: 0, label: '' }],
    },
    course: {
        label: 'Vyberte kurz',
    },
    id: {
        label: null,
        note: null,
    },
    student_name: {
        placeholder: 'Vyplňte..',
        label: 'Jméno Žáka',
        note: 'Vyplňuje se kvůli docházce',
    },
};

const PersonalTestForm: React.FC<{ courses: PersonalTestType[]; packages: PersonalTestPackageType[]; preselectLessonClassId?: string; preselectTermId?: number }> = ({
    courses,
    packages,
    preselectLessonClassId,
    preselectTermId,
}) => {
    const { state, dispatch } = useCoursesState();
    const history = useHistory();

    const [selectedPackageTest, setSelectedPackageTest] = useState<SelectedPackageTestType>();
    const [formInitialValues, setFormInitialValues] = useState(initialValues);

    let selectedCourse: PersonalTestType;

    const locationsIds: number[] = [];
    const locations = useMemo(
        () =>
            courses
                .map((course) => course.terms[0].location)
                .filter((location) => {
                    if (locationsIds.indexOf(location.id) > -1) {
                        return false;
                    }
                    locationsIds.push(location.id);
                    return true;
                }),
        // eslint-disable-next-line
        [courses]
    );

    attributes.selectedLocation.options = locations.map((location) => ({
        value: location.id,
        label: `${location.city.name} - ${location.street_name} ${location.street_number}${
            location.name ? ' · ' + location.name : ''
        }`,
    }));

    useEffect(() => {
        const selectedLocationId = locations.length === 1 ? locations[0].id : 0;
        const filteredCourses = courses.filter(
            (c) => c.terms[0].location.id === selectedLocationId
        );

        let courseName = '';
        let firstTermId = 0;

        if (filteredCourses.length === 1) {
            courseName = filteredCourses[0].name;
            firstTermId = filteredCourses[0].terms[0]?.id || 0;
        }

        if (preselectTermId) {
            for (const course of filteredCourses) {
                const match = course.terms.find((term) => term.id === preselectTermId);
                if (match) {
                    courseName = course.name;
                    firstTermId = match.id;
                    break;
                }
            }
        }

        if (!firstTermId && filteredCourses.length === 1) {
            courseName = filteredCourses[0].name;
            firstTermId = filteredCourses[0].terms[0]?.id || 0;
        }

        setFormInitialValues({
            selectedLocation: selectedLocationId,
            course: courseName,
            id: firstTermId,
            student_name: '',
        });

        if (preselectLessonClassId && ! preselectTermId) {
            const foundCourse = courses.find(course =>
                course.terms.some(term => term.course_key === preselectLessonClassId)
            );
            if (foundCourse) {
                setFormInitialValues({
                    ...formInitialValues,
                    course: foundCourse.name,
                    id: foundCourse.lesson_class?.id ?? firstTermId,
                });

                return;
            }
        }
    }, [courses, locations, preselectLessonClassId, preselectTermId]);

    if (locations.length === 1) {
        initialValues.selectedLocation = locations[0].id;
    }

    const saveSelectedCourse = (values: PersonalTestFormType) => {
        const selectedTerm = selectedCourse.terms.filter((term) => term.id === values.id)[0];
        dispatch({
            type: CoursesTypes.SaveCourse,
            course: { ...values, term: selectedTerm, classLesson: selectedCourse.lesson_class.name },
            courseType: 'personalTest',
        });

        pushDataLayer({ 'ecommerce': null });
        pushDataLayer({
            'event': 'add_to_cart',
            'ecommerce': {
                'value': selectedTerm.price,
                'currency': 'CZK',
                'items': [
                    {
                        'item_id' : 'onsite_test_' + values.id,
                        'item_name': values.course,
                        'price': selectedTerm.price,
                        'quantity': 1,
                        'item_category': 'Přijímačky nanečisto',
                        'item_category2': selectedCourse.lesson_class.name,
                        'item_variant': 'on-site'
                    }
                ]
            }
        });
    };

    const onSubmit = async (values: PersonalTestFormType) => {
        saveSelectedCourse(values);
        if (getCookie('jid')) {
            history.push(routesURLs.selectMore);
        } else {
            history.push(routesURLs.registration);
        }
    };

    const validationSchema = Yup.object().shape({
        selectedLocation: Yup.string().required('Povinné pole'),
        course: Yup.string().required('Povinné pole'),
        id: Yup.number().moreThan(0, 'Povinné pole'),
        student_name: Yup.string().required('Zadejte jméno žáka'),
    });

    if (!courses.length) {
        return null;
    }

    return (
        <Formik initialValues={formInitialValues} enableReinitialize={true} onSubmit={onSubmit} validationSchema={validationSchema}>
            {({ isValid, isSubmitting, values, setFieldValue, errors, submitCount, resetForm }) => {
                const coursesOnLocation = courses.filter(
                    (course) => course.terms[0].location.id === values.selectedLocation
                );
                selectedCourse = coursesOnLocation.filter((course) => course.name === values.course)[0];
                const selectedCourseTerms = selectedCourse ? selectedCourse.terms : [];

                const resetIdAfterCourseSelection = (courseName: string) => {
                    if (courseName !== values.course) {
                        setFieldValue('id', 0);
                        setFieldValue('course', courseName);

                        const selected = coursesOnLocation.find((c) => c.name === courseName);
                        const firstTermId = selected?.terms[0]?.id || 0;
                        if (firstTermId) {
                            setFieldValue('id', firstTermId);
                        }
                    }
                };

                const selectOtherCourse = (values: PersonalTestFormType) => {
                    saveSelectedCourse(values);
                    history.push(routesURLs.selectMore);
                };

                const selectPackageCourse = (course: SelectedPackageTestType) => {
                    setFieldValue('id', 0);
                    setFieldValue('course', '');
                    setSelectedPackageTest(course);
                };

                const coursesInPackage = packages
                    .filter(
                        (packageItem) =>
                            coursesOnLocation.filter((course) => course.terms[0].type.id === packageItem.type.id)
                                .length === packageItem.quantity
                    )
                    .map((packageItem) => ({
                        ...packageItem,
                        courses: coursesOnLocation.filter((course) => course.terms[0].type.id === packageItem.type.id),
                    }))
                    .filter((packageItem) =>
                        packageItem.courses.some(
                            (course) =>
                                !state.savedPersonalTests.filter((savedTest) => savedTest.course === course.name)
                                    .length
                        )
                    );

                return (
                    <>
                        <Form>
                            <FormInputSelect
                                name="selectedLocation"
                                attributes={attributes.selectedLocation}
                                onValueChange={() => resetForm()}
                            />
                            {!!values.selectedLocation && (
                                <>
                                    <label>{attributes.course.label}</label>
                                    <div className="container-row">
                                        {coursesOnLocation.map((course) => (
                                            <div key={course.name} className="col-md-4">
                                                <ImageBox
                                                    image={personCoursesImage}
                                                    color="secondary"
                                                    //title={`${course.name} - ${format(parseISO(course.terms[0].date_from), 'd. MMMM yyyy', { locale: cs })}`}
                                                    title={`${course.lesson_class?.name ?? 'bez výběru třídy'}`}
                                                    //subtitle={`Cena: ${formateNumber(course.terms[0].price, 0)} Kč`}
                                                    small={true}
                                                    isChecked={course.name === values.course}
                                                    onClick={() => resetIdAfterCourseSelection(course.name)}
                                                />
                                            </div>
                                        ))}
                                        {coursesInPackage.map((course) => (
                                            <div key={course.id} className="col-md-4">
                                                <ImageBox
                                                    image={personCoursesImage}
                                                    color="secondary"
                                                    title={`${course.courses.reduce(
                                                        (sum, item) => sum + (sum ? ' + ' : '') + item.name,
                                                        ''
                                                    )}`}
                                                    subtitle={`Cena: ${formateNumber(course.price, 0)} Kč`}
                                                    small={true}
                                                    isChecked={course.id === selectedPackageTest?.id}
                                                    onClick={() => selectPackageCourse(course)}
                                                />
                                            </div>
                                        ))}
                                        {errors.course && !!submitCount && (
                                            <div className="form-input-footnote image-box-error">
                                                <div className="form-error-messages">{errors.course}</div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            {!!selectedCourse && !!values.selectedLocation && (
                                <div className="h-mb-175">
                                    <div className="form-label h-display-block">Vyberte Datum a čas</div>
                                    {selectedCourseTerms.map((term) => (
                                        <FormInputRadio
                                            key={term.id}
                                            name="id"
                                            attributes={{
                                                options: [
                                                    {
                                                        label: term.date_from,
                                                        value: term.id,
                                                    },
                                                ],
                                                disabled: !!state.savedPersonalTests.filter(
                                                    (course) => course.id === term.id
                                                ).length,
                                                ...attributes.id,
                                            }}
                                        >
                                            <CheckboxItem
                                                key={term.id}
                                                text={testTermDescription({
                                                    dateFrom: term.date_from,
                                                    timeFrom: term.time_from || '',
                                                    price: term.price,
                                                })}
                                                note={`(Kapacita: ${term.capacity})`}
                                            />
                                            <></>
                                        </FormInputRadio>
                                    ))}
                                </div>
                            )}
                            <FormInputText name="student_name" attributes={attributes.student_name} />
                            <hr className="h-mt-2" />
                            <div className="personal-courses-form-summary">
                                <LabelValue
                                    label="Celkem zaplatíte"
                                    value={
                                        selectedPackageTest
                                            ? formateNumber(selectedPackageTest.price, 0)
                                            : selectedCourse
                                                ? `${formateNumber(
                                                    selectedCourse.terms.filter((term) => term.id === values.id)[0]
                                                        ?.price || 0,
                                                    0
                                                )} Kč`
                                                : '0 Kč'
                                    }
                                    className="personal-courses-form-summary-price"
                                />
                                <Button
                                    onClick={() => selectOtherCourse(values)}
                                    color="transparent"
                                    disabled={!isValid}
                                    className="h-mr-05"
                                >
                                    Vybrat další kurz
                                </Button>
                                <Button type="submit" color="primary" disabled={!isValid} loading={isSubmitting}>
                                    Pokračovat
                                </Button>
                            </div>
                        </Form>
                        <Modal isOpen={!!selectedPackageTest} close={() => setSelectedPackageTest(undefined)}>
                            {!!selectedPackageTest && <ModalChoosePackageTest data={selectedPackageTest!} />}
                        </Modal>
                    </>
                );
            }}
        </Formik>
    );
};

export default PersonalTestForm;
